import { useState } from "react";
import { useModal, create, show } from "@ebay/nice-modal-react";
import { useMutation } from "@apollo/client";

import FlashContext from "components/FlashMessages/FlashContext";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import Textarea from "ds/components/Textarea";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";

import { DEPRECATE_VERSION } from "./gql";
import { REFETCH_QUERIES_LIST } from "./constants";

type DeprecateConfirmationProps = {
  id: string;
  versionLabel: string;
};

const DeprecateConfirmation = create(function DeprecateConfirmation({
  id,
  versionLabel,
}: DeprecateConfirmationProps) {
  const modal = useModal();
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const [note, setNote] = useState("");

  const [deprecateVersion, { loading, data }] = useMutation(DEPRECATE_VERSION, {
    refetchQueries: REFETCH_QUERIES_LIST,
    awaitRefetchQueries: true,
  });

  const handleConfirm = () => {
    const trimmed = note.trim();
    deprecateVersion({
      variables: {
        id,
        input: { note: trimmed || null },
      },
    })
      .then((result) => {
        if (result.data?.templateVersionDeprecate?.id) {
          reportSuccess({
            message: `Version "${versionLabel}" is now marked as deprecated`,
          });
          modal.resolve();
          modal.hide();
        }
      })
      .catch(onError);
  };

  return (
    <DeleteConfirmationModal
      title="Deprecate version"
      confirmationButtonLabel="Deprecate version"
      onConfirm={handleConfirm}
      isLoading={loading}
      isDismissable={!loading && !data?.templateVersionDeprecate?.id}
    >
      <ConfirmationModalMessage>
        <Typography tag="div">
          <strong>This action cannot be undone.</strong>
        </Typography>
        <Typography tag="div" margin="medium 0 0 0">
          Deprecating marks <strong>{versionLabel}</strong> as deprecated. Existing deployments
          continue to work; you cannot create new deployments from this version.
        </Typography>
        <Typography tag="div" margin="x-large 0">
          If you want, you can leave an explanatory note for your team.
        </Typography>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
      </ConfirmationModalMessage>
    </DeleteConfirmationModal>
  );
});

export const showDeprecateConfirmation = (props: DeprecateConfirmationProps) =>
  show(DeprecateConfirmation, props);
